import { useEffect, useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { applyAccountDefaultImpact, createAccount, destroyAccount, updateAccount } from "../../ApiInterface"
import { useAccounts } from "../../AccountsContext"
import moment from "moment"
import { accountPath, cardStatementName } from "../../Helpers"
import IconPicker from "../../components/IconPicker"
import Modal from "../../components/Modal"
import ImpactPicker from "../BankTransactionsList/ImpactPicker"
import { formatImpact } from "../../Impact"

const AccountNameInput = ({ account, onSave }: { account: Account, onSave: (name: string) => void }) => {
    const [name, setName] = useState(account.name)
    useEffect(() => setName(account.name), [account.name])

    const save = () => {
        if(name.trim() === '') {
            setName(account.name)
        }
        else if(name.trim() !== account.name) {
            onSave(name.trim())
        }
    }

    return <input
        className="tl-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if(e.key === 'Enter') { e.currentTarget.blur() } }}/>
}

// Impact donné aux nouvelles opérations du compte (synchro, saisie) : même sélecteur que dans les relevés.
const DefaultImpactInput = ({ account, onSave }: { account: Account, onSave: (impact: Impact) => void }) =>
    <span className="default-impact" title="Impact des nouvelles opérations de ce compte (ex. ½ pour un compte joint)">
        <span className="muted">Impact</span>
        <ImpactPicker value={account.default_impact} onChange={onSave}/>
    </span>

// Comptes de la navigation : renommage, icône, création d'un compte saisi à la main et d'un relevé
// carte (ouvert vide, pour y saisir des opérations).
const AccountsSettings = ({ onError }: { onError: (fallback: string) => (e: any) => void }) => {
    const { accounts, reloadAccounts } = useAccounts()
    const navigate = useNavigate()
    const [newAccountName, setNewAccountName] = useState('')
    const [newStatementAccount, setNewStatementAccount] = useState(null as Account | null)
    const [newStatementMonth, setNewStatementMonth] = useState('')
    const [accountToRemove, setAccountToRemove] = useState(null as Account | null)
    const [removing, setRemoving] = useState(false)
    const [impactAccount, setImpactAccount] = useState(null as Account | null)
    const [applyingImpact, setApplyingImpact] = useState(false)

    const save = (account: Account, params: AccountParams) => updateAccount(account.id, params)
        .then(reloadAccounts)
        .catch(onError('L\'enregistrement du compte a échoué'))

    const create = (e: React.FormEvent) => {
        e.preventDefault()
        if(newAccountName.trim() === '') {
            return
        }
        createAccount({ name: newAccountName.trim() }).then(() => {
            setNewAccountName('')
            reloadAccounts()
        }).catch(onError('Impossible de créer ce compte'))
    }

    const remove = (account: Account) => {
        setRemoving(true)
        destroyAccount(account.id)
            .then(reloadAccounts)
            .then(() => setAccountToRemove(null))
            .catch(onError('Impossible de supprimer ce compte'))
            .finally(() => setRemoving(false))
    }

    const applyImpact = (account: Account) => {
        setApplyingImpact(true)
        applyAccountDefaultImpact(account.id)
            .then(reloadAccounts)
            .then(() => setImpactAccount(null))
            .catch(onError('Impossible d\'appliquer l\'impact par défaut'))
            .finally(() => setApplyingImpact(false))
    }

    const removeCount = accountToRemove ? accountToRemove.statements.reduce((sum, statement) => sum + statement.count, 0) : 0
    const removeStatementsCount = accountToRemove ? accountToRemove.statements.length : 0

    const createStatement = (e: React.FormEvent) => {
        e.preventDefault()
        if(newStatementAccount && newStatementMonth !== '') {
            navigate(accountPath(newStatementAccount, cardStatementName(newStatementMonth)))
        }
    }

    // Par défaut, le mois de débit qui suit le dernier relevé.
    const openNewStatement = (account: Account) => {
        const period = account.statements.find((statement) => statement.period)?.period
        const next = period ? moment({ year: period.year, month: period.end_month - 1 }).add(1, 'month') : moment()
        setNewStatementMonth(next.format('YYYY-MM'))
        setNewStatementAccount(account)
    }

    return <div className="tl-card accounts-settings">
        { newStatementAccount && <Modal onCancel={() => setNewStatementAccount(null)}>
            <form className="new-filename-form" onSubmit={createStatement}>
                <h3>Nouveau relevé · { newStatementAccount.name }</h3>
                <div className="tl-field">
                    <label htmlFor="new-statement-month">Mois de débit</label>
                    <input id="new-statement-month" type="month" className="tl-input"
                        value={newStatementMonth} onChange={(e) => setNewStatementMonth(e.target.value)} autoFocus/>
                </div>
                { newStatementMonth && <p className="statement-name">{ cardStatementName(newStatementMonth) }</p> }
                <div className="buttons">
                    <button type="button" className="tl-btn tl-btn-ghost" onClick={() => setNewStatementAccount(null)}>Annuler</button>
                    <button type="submit" className="tl-btn tl-btn-primary">Créer</button>
                </div>
            </form>
        </Modal> }

        { impactAccount && <Modal onCancel={() => setImpactAccount(null)}>
            <div className="remove-account-modal">
                <h3>Impact à { formatImpact(impactAccount.default_impact) } · { impactAccount.name }</h3>
                <p>
                    { impactAccount.impact_mismatch_count } opération{ impactAccount.impact_mismatch_count > 1 ? 's' : '' } existante{ impactAccount.impact_mismatch_count > 1 ? 's' : '' } de
                    ce compte passer{ impactAccount.impact_mismatch_count > 1 ? 'ont' : 'a' } à { formatImpact(impactAccount.default_impact) }, y compris celles dont tu as changé l'impact à la main.
                </p>
                <p className="muted">Les opérations liées à une dépense Tricount gardent leur part Tricount.</p>
                <div className="buttons">
                    <button type="button" className="tl-btn tl-btn-ghost" onClick={() => setImpactAccount(null)}>Annuler</button>
                    <button type="button" className="tl-btn tl-btn-primary" disabled={applyingImpact} onClick={() => applyImpact(impactAccount)} autoFocus>
                        { applyingImpact ? 'Application…' : 'Appliquer' }
                    </button>
                </div>
            </div>
        </Modal> }

        { accountToRemove && <Modal onCancel={() => setAccountToRemove(null)}>
            <div className="remove-account-modal">
                <h3>Supprimer « { accountToRemove.name } » ?</h3>
                { removeCount === 0
                    ? <p>Ce compte est vide.</p>
                    : <>
                        <p className="tl-alert tl-alert-danger">
                            Ses { removeCount } opération{ removeCount > 1 ? 's' : '' }, dans { removeStatementsCount } relevé{ removeStatementsCount > 1 ? 's' : '' },
                            seront supprimées définitivement.
                        </p>
                        <p className="muted">Les opérations liées à un autre compte ne sont pas supprimées : elles restent dans cet autre compte, seul le lien est retiré.</p>
                    </> }
                <div className="buttons">
                    <button type="button" className="tl-btn tl-btn-ghost" onClick={() => setAccountToRemove(null)}>Annuler</button>
                    <button type="button" className="tl-btn tl-btn-primary" disabled={removing} onClick={() => remove(accountToRemove)} autoFocus>
                        { removing ? 'Suppression…' : 'Supprimer le compte' }
                    </button>
                </div>
            </div>
        </Modal> }

        <h3>Comptes</h3>
        <div className="sub">Les comptes et leurs relevés, dans le menu de gauche</div>

        <table className="accounts">
            <tbody>
                { accounts.map((account) => <tr key={account.id}>
                    <td className="icon">
                        <IconPicker icon={account.icon} color={account.color} update={(params) => save(account, params)}/>
                    </td>
                    <td className="name">
                        <AccountNameInput account={account} onSave={(name) => save(account, { name })}/>
                    </td>
                    <td className="source">
                        { account.synced
                            ? <span className="tl-badge tl-badge-ok" title={account.bank_account_name || ''}>Synchronisé</span>
                            : <span className="muted">Saisi à la main</span> }
                    </td>
                    <td className="impact">
                        { !account.tricount && <DefaultImpactInput account={account} onSave={(default_impact) => save(account, { default_impact })}/> }
                        { !account.tricount && account.impact_mismatch_count > 0 && <button type="button" className="apply-impact"
                            title={`Passer les opérations existantes à ${formatImpact(account.default_impact)}`}
                            onClick={() => setImpactAccount(account)}>
                            Appliquer aux { account.impact_mismatch_count } existante{ account.impact_mismatch_count > 1 ? 's' : '' }
                        </button> }
                    </td>
                    <td className="statements">
                        <Link to={accountPath(account)}>
                            { account.statements.length } relevé{ account.statements.length > 1 ? 's' : '' }
                        </Link>
                    </td>
                    <td className="actions">
                        { account.card && <button className="tl-btn tl-btn-secondary" onClick={() => openNewStatement(account)}>
                            + Relevé
                        </button> }
                        { !account.synced && <button className="tl-btn tl-btn-ghost" onClick={() => setAccountToRemove(account)}>
                            Supprimer
                        </button> }
                    </td>
                </tr>) }
            </tbody>
        </table>

        <form className="new-account" onSubmit={create}>
            <input className="tl-input" placeholder="Nouveau compte saisi à la main (ex. Tricount vacances)"
                value={newAccountName} onChange={(e) => setNewAccountName(e.target.value)}/>
            <button type="submit" className="tl-btn tl-btn-primary" disabled={newAccountName.trim() === ''}>Créer le compte</button>
        </form>
    </div>
}

export default AccountsSettings
